/**
 * What the Movement Lab puts on the hold-Tab wheel.
 *
 * `5fc7474` made the lab a bench by stocking the *stash* with the whole
 * catalogue. The radial is a different list: `uiRoot.buildRadial` draws eight
 * fixed wedges (docs/04 §7) fed by `inventory.slots`, and `uiAdapter` fills
 * those from the two hands followed by what the run is carrying. Stocking the
 * stash therefore never touched the wheel, and in the lab the wheel was still
 * whatever the kit happened to issue — one sword and one pistol, six empty
 * wedges.
 *
 * ## Eight wedges cannot hold thirty weapons, so the wheel spends a slot on a
 * ## *motion*, not on a weapon
 *
 * The lab exists to compare weapons, and a wheel of eight near-identical
 * swords compares nothing. The catalogue's thirty weapons draw only eleven
 * distinct motions between them, which is a number eight is close to — so the
 * wheel samples motions and lets the bench (one panel away, and stocked with
 * everything) hold the rest.
 *
 * A **motion** here is what `weaponVisual.visualForClass` decides, and it is
 * deliberately not the bare `SwingStyle` field: that function returns a style
 * *and* a segment count, and `segments > 1` is documented there as what makes
 * a weapon flexible. Five segments of chain thrown out on a `thrust` is not
 * the drawing a spear makes on the same style — `dredge_anchor`'s own comment
 * in `weaponData.ts` says exactly that ("what differs is the object on the end
 * of it, which is what `segments` and `thickness` are for"). Splitting on it
 * costs one line and adds exactly one distinction to today's catalogue: the
 * anchor's.
 *
 * ## The ranking, and what it is derived from
 *
 * Nothing here names a weapon. Every key is read off the catalogue and off
 * `weaponVisual`, so a weapon added tomorrow is ranked without anybody
 * remembering this file — the failure this project has found fifteen times,
 * running the other way as an authored field nothing reads.
 *
 *   1. **Rarest motion first**, counted over the whole catalogue. The rarest
 *      motion is the one you cannot see any other way; `arc` (three weapons,
 *      and the sword already in your main hand is one) and `none` (thirteen,
 *      every projectile) need no wedge spent on them.
 *   2. **Body-pivoted before hand-pivoted.** `weaponVisual.ts` splits
 *      `SwingStyle` into those two groups under two headings and argues that
 *      the body-pivoted three were previously inexpressible. They are also the
 *      three whose drawing is a function of where the *body* is, which is the
 *      one thing a Movement Lab can evaluate that a static bench cannot.
 *   3. **Longest reach first**, then catalogue order. Reach is the wheel's
 *      other axis, and it is the tiebreak both between equally-ranked motions
 *      and between the weapons inside one.
 *
 * The hands count against the eight, because slots 0 and 1 *are* the hands —
 * that is what `inventory.slots.includes(id)` means to the loadout panel. So
 * the kit's sword and pistol are two of the wedges (the `arc` control and the
 * projectile control), and this returns the other six.
 *
 * ## It cannot touch a real run
 *
 * The scene is the first parameter and the first thing read, so the lab-only
 * rule is a property of the function rather than of its one call site. Any
 * scene but `lab` gets an empty list and the run's wheel stays exactly what
 * the run picked up.
 */

import type { WeaponDef } from '../combat/types';
import { visualForClass, swingPose } from '../entity/weaponVisual';
import { MAT } from '../sim/materialData';

/**
 * Wedges on the radial — docs/04 §7, and `uiAdapter`'s own `WEAPON_SLOTS`,
 * which is module-private there. `tests/labWheel.test.ts` scans that file and
 * fails if the two ever disagree, because a wheel sized against the wrong
 * number silently drops its tail.
 */
export const WHEEL_SLOTS = 8;

/**
 * `visualForClass` wants a material for the weapon's colour. The motion does
 * not depend on it, and this module only ever reads the motion.
 */
const ANY_MAT = MAT.STEEL;

/**
 * The motion a weapon is seen to make: its swing style, plus whether it is
 * flexible. See the header for why the segment count is part of the identity.
 */
export function motionOf(def: WeaponDef): string {
  const v = visualForClass(String(def.class), def.reach, def.weight, ANY_MAT);
  return `${v.style}|${v.segments > 1 ? 'flex' : 'rigid'}`;
}

/**
 * Whether the motion turns about the figure rather than about the grip.
 *
 * Read off `swingPose` rather than listed, so a style that gains or loses its
 * body pivot re-ranks itself. Sampled across the swing because the flag is a
 * function of `t` in principle; `swingPose` returns a shared object, so each
 * sample is read before the next call.
 */
function bodyPivoted(def: WeaponDef): boolean {
  const style = visualForClass(String(def.class), def.reach, def.weight, ANY_MAT).style;
  for (const t of [0, 0.25, 0.5, 0.75, 1]) {
    if (swingPose(style, t).bodyPivot) return true;
  }
  return false;
}

/**
 * The weapons the lab should carry so the wheel shows eight different motions.
 *
 * `inHand` is what the kit already put in the hands — those occupy the first
 * wedges and their motions are already covered, so they are neither returned
 * nor counted twice. The result is in wheel order and never includes anything
 * already in hand.
 */
export function labWheelDefs(
  scene: 'world' | 'lab' | 'prologue',
  catalogue: readonly WeaponDef[],
  inHand: readonly (WeaponDef | null | undefined)[] = [],
  capacity: number = WHEEL_SLOTS,
): WeaponDef[] {
  // The guard, first and unconditional. A real run's wheel is what the run
  // picked up, and nothing below runs for it.
  if (scene !== 'lab') return [];

  // One wedge per *hand*, not per distinct definition: two rolls of the same
  // weapon are two instances and the adapter gives them a slot each (#12), so
  // deduping here would ask for nine wedges and lose the ninth silently.
  const inHandDefs = inHand.filter((d): d is WeaponDef => !!d);
  const room = capacity - inHandDefs.length;
  if (room <= 0) return [];

  const order = new Map<string, number>();
  catalogue.forEach((d, i) => order.set(d.id, i));

  // Population is counted over the *whole* catalogue: how rare a motion is, is
  // a fact about the game and not about what you happen to be holding.
  const population = new Map<string, number>();
  for (const d of catalogue) {
    const m = motionOf(d);
    population.set(m, (population.get(m) ?? 0) + 1);
  }

  const heldMotions = new Set<string>();
  for (const d of inHandDefs) heldMotions.add(motionOf(d));

  const groups = new Map<string, WeaponDef[]>();
  for (const d of catalogue) {
    const m = motionOf(d);
    // Skipping the motion already skips the weapon making it — a `held.has(id)`
    // guard here would be unreachable, and an unreachable guard is the kind of
    // line that reads as a safety net while testing nothing.
    if (heldMotions.has(m)) continue;
    const g = groups.get(m);
    if (g) g.push(d);
    else groups.set(m, [d]);
  }

  // Inside a motion: the longest reach is its most legible instance, then
  // catalogue order so the result is total and stable.
  const byReach = (a: WeaponDef, b: WeaponDef): number =>
    b.reach - a.reach || (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
  for (const g of groups.values()) g.sort(byReach);

  const ranked = [...groups.entries()].sort(([ma, ga], [mb, gb]) => {
    const pa = population.get(ma) ?? 0;
    const pb = population.get(mb) ?? 0;
    if (pa !== pb) return pa - pb;
    const ba = bodyPivoted(ga[0]) ? 0 : 1;
    const bb = bodyPivoted(gb[0]) ? 0 : 1;
    if (ba !== bb) return ba - bb;
    return byReach(ga[0], gb[0]);
  });

  // One per motion, rarest first. The second pass only ever runs on a
  // catalogue with fewer motions than wedges — today there are eleven and at
  // most eight wedges — and exists so a shrunken catalogue still fills the
  // wheel instead of leaving it half empty.
  const picked: WeaponDef[] = [];
  for (let pass = 0; picked.length < room; pass++) {
    let took = 0;
    for (const [, g] of ranked) {
      if (picked.length >= room) break;
      if (pass >= g.length) continue;
      picked.push(g[pass]);
      took++;
    }
    if (took === 0) break;
  }
  return picked;
}
